import React from 'react';

import { EditorState } from 'draft-js';
import PropTypes from 'prop-types';

import theme, { getInteractiveStyles } from '@adsk/alloy-react-theme';

import MentionPlugin, { MentionPluginProps } from './MentionPlugin';

type Booleanish = boolean | 'true' | 'false';

export type MentionTextareaProps = Omit<MentionPluginProps, 'spellCheck'> & {
  spellCheck?: Booleanish;
  error?: string;
  disabled?: boolean;
  focused?: boolean;
  maxHeight?: number | string;
  minHeight?: number | string;
};

const MentionTextarea = ({
  className,
  style,
  focused,
  error,
  spellCheck,
  disabled = false,
  maxHeight = '100%',
  minHeight = 36,
  ...props
}: MentionTextareaProps) => (
  <MentionPlugin
    {...props}
    className={className}
    readOnly={disabled}
    style={[
      {
        ...theme.typography.labelMedium,
        color: theme.colors.charcoal900,
        border: `1px solid ${theme.colors.charcoal400}`,
        borderRadius: 2,
        boxSizing: 'border-box',
        maxHeight,
        minHeight,
        outline: 'none',
        padding: '8px 12px',
        overflow: 'auto',

        ...getInteractiveStyles(disabled),

        ...(disabled && {
          background: theme.colors.charcoal050,
          borderColor: theme.colors.charcoal200,
          boxShadow: 'none',
          color: theme.colors.charcoal500,
          cursor: 'not-allowed',
          overflow: 'hidden',
        }),

        ...(!error && {
          '&:focus:not(:active)': {
            borderColor: theme.colors.adskBlue500,
          },
        }),

        ...(error && {
          borderColor: theme.colors.red600,
        }),

        ...(focused && {
          boxShadow: `0 0 0 2px ${theme.colors.focusHalo}`,
        }),
      },
      style,
    ]}
  />
);

MentionTextarea.displayName = 'MentionTextarea';

MentionTextarea.propTypes = {
  ...MentionPlugin.propTypes,
  /** Toggle spellCheck state */
  spellCheck: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Toggle focused state */
  focused: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Max height in pixels or percent (applied via CSS) */
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Min height in pixels or percent (applied via CSS) */
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default Object.assign(MentionTextarea, {
  PLACEMENTS: MentionPlugin.PLACEMENTS,
  EMPTY_STATE: EditorState.createEmpty(),
  TYPES: MentionPlugin.TYPES,
});
